import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { newsData } from '../../mocks/news';
import { reviewsData } from '../../mocks/reviews';
import { faqData } from '../../mocks/faq';
import { SEO } from '../../utils/seo';

export default function HomePage() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'おかやま健康製薬株式会社',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    description: '岡山市の健康サプリメント専門メーカー。全世代の健康をサポートする高品質サプリメントを提供。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北区○○町1-2-3',
      addressLocality: '岡山市',
      addressRegion: '岡山県',
      postalCode: '700-0000',
      addressCountry: 'JP',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '34.6617',
      longitude: '133.9350',
    },
    telephone: '+81-86-012-3456',
    faxNumber: '+81-86-012-3457',
    email: 'info@okayama-health.co.jp',
    foundingDate: '2000',
    numberOfEmployees: 5,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    areaServed: {
      '@type': 'Country',
      name: 'Japan',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '岡山の健康サプリメント専門メーカー | 全世代の健康をサポート | おかやま健康製薬株式会社',
    description: '岡山市のおかやま健康製薬株式会社は、10代から全国へ。全世代の健康をサポートする信頼のサプリメント',
    url: `${siteUrl}/`,
    inLanguage: 'ja-JP',
    isPartOf: {
      '@type': 'WebSite',
      name: 'おかやま健康製薬株式会社',
      url: siteUrl,
    },
  };

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [organizationSchema, faqSchema, webPageSchema],
  };

  const heroSlides = [
    {
      id: 1,
      image: 'https://public.readdy.ai/ai/img_res/79667e9403fd5e6290c122b7b3260d5f.jpg',
      title: '家族の健康を、毎日の習慣に',
      subtitle: '岡山から全国へ。全世代の健康をサポートする信頼のサプリメント',
    },
    {
      id: 2,
      image: 'https://readdy.ai/api/search-image?query=beautiful%20japanese%20woman%20in%20her%20thirties%20holding%20supplement%20bottle%20with%20gentle%20smile%20standing%20by%20window%20in%20morning%20sunlight%20wearing%20casual%20comfortable%20clothing%20peaceful%20atmosphere%20healthy%20lifestyle%20photography%20natural%20lighting%20high%20resolution&width=1920&height=1080&seq=hero2&orientation=landscape',
      title: 'あなたに合った健康習慣を',
      subtitle: '年齢や目的に応じた最適なサプリメントで、理想の健康生活を実現',
    },
    {
      id: 3,
      image: 'https://readdy.ai/api/search-image?query=active%20japanese%20senior%20couple%20in%20their%20sixties%20walking%20together%20in%20beautiful%20park%20with%20green%20trees%20smiling%20happily%20wearing%20sporty%20casual%20clothes%20enjoying%20healthy%20active%20lifestyle%20bright%20sunny%20day%20photography%20high%20quality&width=1920&height=1080&seq=hero3&orientation=landscape',
      title: 'いつまでも元気に、笑顔で',
      subtitle: '科学的根拠に基づいた高品質サプリメントで、健やかな毎日をサポート',
    },
  ];

  const mainProducts = [
    {
      id: 1,
      title: 'ユース世代向け',
      subtitle: '10代-20代',
      description: '成長期から社会人デビューまでの活力をサポート。ビタミンB群、鉄分、亜鉛で元気な毎日を。',
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d7bb4e46333ac73348a901dd4c42e7de.webp',
      features: ['ビタミンB群', '鉄分・亜鉛', '集中力サポート'],
    },
    {
      id: 2,
      title: 'ミドル世代向け',
      subtitle: '30代-50代',
      description: '忙しいビジネスパーソンの疲労回復をサポート。コエンザイムQ10、マカで活力をチャージ。',
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/0b92ebc55a5eb36ad78c9f7675f1acd5.jpeg',
      features: ['疲労回復', 'ストレス対策', '活力チャージ'],
    },
    {
      id: 3,
      title: 'シニア世代向け',
      subtitle: '50代以上',
      description: 'いつまでも元気に過ごすために。グルコサミン、コンドロイチン、カルシウムで健康維持。',
      image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d0f4b59c49e52a9603c8b21ae4188ca2.jpeg',
      features: ['関節サポート', '骨の健康', '記憶力サポート'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="岡山の健康サプリメント専門メーカー | 全世代の健康をサポート | おかやま健康製薬株式会社"
        description="岡山市のおかやま健康製薬株式会社は、10代から高齢者まで全世代の健康をサポートする高品質サプリメントを提供。若年層向け、働き盛り世代向け、シニア世代向けの3タイプを主力商品として、妊婦向け、成長期の子供向け、栄養補助系サプリメントも豊富に取り揃えています。安心・安全な品質管理で家族の健康を守ります。"
        keywords="岡山,サプリメント,健康食品,栄養補助,全世代対応,おかやま健康製薬"
        canonical={`${siteUrl}/`}
        schema={combinedSchema}
      />
      <Header />

      {/* Hero Slider Section */}
      <section className="relative h-[85vh] mt-16 lg:mt-20">
        <Swiper
          modules={[Autoplay, Pagination, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop={true}
          className="h-full"
        >
          {heroSlides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className="relative h-full w-full">
                <div className="absolute inset-0">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="relative h-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-20">
                  <div className="relative w-full lg:w-1/2 text-white">
                    <div className="absolute inset-0 -left-[100vw] bg-[#4A90E2]/40"></div>
                    <div className="relative py-8 sm:py-10 lg:py-12 pr-6 sm:pr-8 lg:pr-10">
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 lg:mb-6 leading-tight">
                        {slide.title}
                      </h1>
                      <p className="text-base sm:text-lg lg:text-xl mb-8 lg:mb-10 text-white/90 leading-relaxed">
                        {slide.subtitle}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Link
                          to="/products"
                          className="inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-white text-[#1A4D6D] rounded-lg font-medium hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-shopping-bag-line mr-2 text-lg"></i>
                          商品を見る
                        </Link>
                        <Link
                          to="/contact"
                          className="inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-[#4A90E2] text-white rounded-lg font-medium hover:bg-[#2A6D8D] transition-all shadow-lg whitespace-nowrap"
                        >
                          お問い合わせフォーム
                          <i className="ri-arrow-right-line ml-2 text-lg"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* News Section */}
      <section className="py-12 lg:py-20 bg-[#F5FAFE]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">お知らせ</h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {newsData.map((news) => (
              <article
                key={news.id}
                className="bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm text-gray-600">{news.date}</span>
                  <span className="px-3 py-1 bg-[#E8F4F8] text-[#4A90E2] text-xs font-medium rounded-lg">
                    {news.category}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-[#1A4D6D] mb-3 line-clamp-2">
                  {news.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{news.excerpt}</p>
                <div className="flex items-center text-[#4A90E2] text-sm font-medium">
                  詳しく見る
                  <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">
              あなたに合ったサプリメントを
            </h2>
            <p className="text-base lg:text-lg text-gray-600">
              年齢・目的別に選べる豊富なラインナップ
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-10 lg:mb-12">
            {mainProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-[3/4] w-full h-64 sm:h-80 lg:h-96">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D]">
                      {product.title}
                    </h3>
                    <span className="text-sm text-gray-500">{product.subtitle}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {product.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[#E8F4F8] text-[#1A4D6D] text-xs font-medium rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/products"
                    className="inline-flex items-center justify-center w-full px-6 py-3 border-2 border-[#4A90E2] text-[#4A90E2] rounded-lg font-medium hover:bg-[#4A90E2] hover:text-white transition-all whitespace-nowrap"
                  >
                    詳細を見る
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center justify-center px-8 lg:px-12 py-3 lg:py-4 border-2 border-[#1A4D6D] text-[#1A4D6D] rounded-full font-medium hover:bg-[#1A4D6D] hover:text-white transition-all whitespace-nowrap"
            >
              全商品を見る
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-[#E8F4F8] to-[#D0E8F2]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="w-full h-64 sm:h-80 lg:h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20japanese%20scientist%20in%20white%20lab%20coat%20carefully%20examining%20supplement%20quality%20in%20modern%20clean%20laboratory%20with%20advanced%20equipment%20focused%20expression%20bright%20sterile%20environment%20scientific%20research%20photography%20natural%20lighting%20high%20resolution&width=700&height=900&seq=philosophy1&orientation=portrait"
                  alt="品質管理"
                  className="w-full h-full object-cover object-top rounded-3xl shadow-2xl"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 bg-[#4A90E2] rounded-full"></div>
                <span className="text-[#4A90E2] font-medium text-sm lg:text-base">
                  私たちの想い
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-6 leading-tight">
                健康は、毎日の小さな選択から生まれる
              </h2>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-6">
                おかやま健康製薬は、2000年の創業以来、「家族の健康を守る」という想いを胸に、高品質なサプリメントを提供し続けてきました。岡山の地から全国のご家庭へ、安心・安全な製品をお届けすることが私たちの使命です。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-8">
                10代の成長期から、働き盛りの30代・40代、そして人生を謳歌するシニア世代まで、すべての年齢層の健康をサポートできるよう、科学的根拠に基づいた製品開発を行っています。
              </p>
              <Link
                to="/philosophy"
                className="inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-[#1A4D6D] text-white rounded-lg font-medium hover:bg-[#0F3A52] transition-all shadow-lg whitespace-nowrap"
              >
                私たちの想いを詳しく
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">お客様の声</h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviewsData.map((review) => (
              <article
                key={review.id}
                className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-6 line-clamp-4">
                  {review.comment}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 flex items-center justify-center">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-[#1A4D6D] text-sm">{review.name}</p>
                    <p className="text-xs text-gray-500">
                      {review.age}歳・{review.gender}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 lg:py-20 bg-[#F5FAFE]">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">
              よくある質問
            </h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto"></div>
          </div>
          <div className="space-y-4">
            {faqData.map((faq) => (
              <article key={faq.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaqId(openFaqId === faq.id ? null : faq.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#4A90E2] text-white rounded-full font-bold text-sm flex-shrink-0">
                      Q
                    </div>
                    <span className="font-bold text-[#1A4D6D] text-sm lg:text-base pr-4">
                      {faq.question}
                    </span>
                  </div>
                  <i
                    className={`ri-arrow-${
                      openFaqId === faq.id ? 'up' : 'down'
                    }-s-line text-2xl text-[#4A90E2] flex-shrink-0`}
                  ></i>
                </button>
                {openFaqId === faq.id && (
                  <div className="px-6 pb-5 bg-[#F5FAFE]">
                    <div className="flex items-start gap-4 pl-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1A4D6D] text-white rounded-full font-bold text-sm flex-shrink-0">
                        A
                      </div>
                      <p className="text-sm lg:text-base text-gray-700 leading-relaxed pt-1">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}